/* biome-ignore-all lint/suspicious/noExplicitAny: terminal log formatting */
import { Empty, Button, Tooltip, message } from "antd"
import { CopyOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons"
import { useRef } from "react"
import type React from "react"
import type { SystemLogItem } from "@/services/log/systemLogApi"
import dayjs from "dayjs"

interface SystemLogTerminalViewProps {
  data: SystemLogItem[]
  loading?: boolean
  onViewDetail: (record: SystemLogItem) => void
}

export const SystemLogTerminalView: React.FC<SystemLogTerminalViewProps> = ({ data, onViewDetail }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const handleScrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }

  const handleCopyAll = () => {
    if (!data || data.length === 0) return
    const text = data.map((d) => d.raw || JSON.stringify(d)).join("\n")
    navigator.clipboard.writeText(text)
    message.success("Đã sao chép tất cả các dòng log trên trang này!")
  }

  const getLevelColor = (level: number) => {
    if (level >= 50) return "text-red-400 font-bold"
    if (level >= 40) return "text-yellow-400 font-semibold"
    if (level >= 30) return "text-cyan-400"
    return "text-purple-400"
  }

  const getLevelText = (level: number) => {
    if (level >= 60) return "FATAL"
    if (level >= 50) return "ERROR"
    if (level >= 40) return "WARN "
    if (level >= 30) return "INFO "
    return "DEBUG"
  }

  return (
    <div className="relative flex h-[600px] flex-col overflow-hidden rounded-lg border border-gray-800 bg-gray-950 text-gray-200 shadow-inner">
      {/* Terminal Header */}
      <div className="flex select-none items-center justify-between border-gray-800 border-b bg-gray-900 px-4 py-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded-full bg-red-500/80" />
            <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
            <div className="h-3 w-3 rounded-full bg-green-500/80" />
          </div>
          <span className="ml-2 font-mono text-gray-400 text-xs">console.log stream ({data.length} records)</span>
        </div>

        <div className="flex items-center gap-2">
          <Tooltip title="Cuộn xuống cuối">
            <Button
              type="text"
              size="small"
              icon={<VerticalAlignBottomOutlined className="text-gray-400 hover:text-white" />}
              onClick={handleScrollToBottom}
            />
          </Tooltip>
          <Tooltip title="Sao chép toàn bộ trang log">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined className="text-gray-400 hover:text-white" />}
              onClick={handleCopyAll}
            />
          </Tooltip>
        </div>
      </div>

      {/* Terminal Content */}
      <div
        ref={containerRef}
        className="flex-1 select-text space-y-1 overflow-auto scroll-smooth p-3 font-mono text-xs leading-relaxed"
      >
        {data.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Empty description={<span className="text-gray-500">Không có dòng log nào phù hợp bộ lọc</span>} />
          </div>
        ) : (
          data.map((item, index) => {
            const timeStr = item.time ? dayjs(item.time).format("YYYY-MM-DD HH:mm:ss.SSS") : "---"
            const levelClass = getLevelColor(item.level || 30)
            const levelStr = getLevelText(item.level || 30)
            const statusCode = item.err?.statusCode || item.res?.statusCode || item.statusCode
            const msg = item.err?.message || item.msg || item.raw || ""

            return (
              <div
                key={item._id || index}
                onClick={() => onViewDetail(item)}
                className="group flex cursor-pointer items-start gap-2.5 rounded px-1.5 py-1 transition-colors hover:bg-gray-800/60"
              >
                {/* Line number */}
                <span className="w-8 shrink-0 select-none text-right text-gray-600">{item.lineIndex || index + 1}</span>

                {/* Timestamp */}
                <span className="shrink-0 text-gray-400">{timeStr}</span>

                {/* Level */}
                <span className={`${levelClass} shrink-0`}>[{levelStr}]</span>

                {/* Status code */}
                {statusCode && <span className="shrink-0 font-semibold text-amber-300">({statusCode})</span>}

                {/* Error type */}
                {item.err?.type && (
                  <span className="shrink-0 font-semibold text-rose-400">&lt;{item.err.type}&gt;</span>
                )}

                {/* Message */}
                <span className="flex-1 break-all text-gray-200 transition-colors group-hover:text-blue-300">
                  {msg}
                </span>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default SystemLogTerminalView
